import { ChangeEventHandler, useEffect, useState } from 'react';
import { snakeCase } from 'change-case';
import { useFormContext, useWatch } from 'react-hook-form';
import { CgChevronDown, CgClose } from 'react-icons/cg';
import {
  IconButton,
  NativeSelect,
  NativeSelectFieldProps,
  NativeSelectIndicatorProps,
  NativeSelectRootProps,
} from '@chakra-ui/react';

import { InputGroup } from '../../lib/chakraUi/components';
import {
  FormFieldUncontrolledComponent,
  FormFieldUncontrolledComponentProps,
} from '../../types/fields';
import { Option, OptionGroup, OptionValue } from '../../types/utility';
import { findOptionByValue } from '../../utils/general';

export type SelectProps = Omit<
  NativeSelectRootProps,
  'onChange' | 'onBlur' | 'onFocus'
> &
  Pick<
    NativeSelectFieldProps,
    'placeholder' | 'onChange' | 'onBlur' | 'onFocus' | 'id' | 'name'
  > & {
    iconSize?: NativeSelectIndicatorProps['boxSize'];
    iconColor?: NativeSelectIndicatorProps['color'];
    groups?: Array<OptionGroup<OptionValue>>;
    multiple?: boolean;
    options?: Array<Option<OptionValue>>;
    asyncOptions?: () => Promise<Pick<SelectProps, 'groups' | 'options'>>;
    hidePlaceholder?: boolean;
    isClearable?: boolean;
  };

const findSelectedOption = (
  value: OptionValue,
  lookupArrays: {
    groups?: Array<OptionGroup<OptionValue>>;
    options?: Array<Option<OptionValue>>;
  },
): Option<OptionValue> | undefined => {
  const { groups, options } = lookupArrays;
  let option: Option<OptionValue> | undefined = findOptionByValue(
    options || [],
    value,
  );
  if (option) return option;

  if (groups) {
    for (const group of groups) {
      option = findOptionByValue(group.options, value);
      if (option) return option;
    }
  }
  return undefined;
};

const hasOptionMeta = (
  value: string,
  options: Array<Option<OptionValue>> | undefined,
) => {
  const selectedOption = options?.find((option) => option.value === value);
  if (selectedOption && selectedOption.meta) {
    const { meta } = selectedOption;
    if (meta !== null) return meta;
  }
  return false;
};

const setRelatedFieldsFromMeta = (
  formData: any,
  meta: any,
  form: any,
  name: any,
) => {
  if (meta) {
    Object.keys(formData).forEach((section: string) => {
      if (formData[section] !== undefined) {
        Object.keys(formData[section]).forEach((id: string) => {
          Object.keys(meta).forEach((metaKey: string) => {
            const [fieldSection, ,] = name.split('.');
            const [, uuid] = name.split('.');
            const fieldWithMetaKey = `${fieldSection}.${uuid}.${name.split('.').pop()}_meta_${snakeCase(metaKey)}`;
            form.setValue(fieldWithMetaKey, meta[metaKey], {
              shouldDirty: true,
            });
          });
        });
      }
    });
  }
};

export const Select: FormFieldUncontrolledComponent<SelectProps> = ({
  onChange: _onChange,
  _ref,
  hooks,
  id,
  name,
  placeholder,
  onBlur,
  onFocus,
  hidePlaceholder,
  isClearable,
  groups: initialGroups,
  options: initialOptions,
  iconSize = '2rem',
  iconColor,
  defaultValue,
  ...props
}: FormFieldUncontrolledComponentProps<SelectProps>) => {
  const [groups, setGroups] = useState(initialGroups);
  const [options, setOptions] = useState(initialOptions);
  const form = useFormContext();
  const value = useWatch({ name });
  const formValues: any = form ? form.getValues() : [];

  const resolveAsyncOptions = async (): Promise<void> => {
    if (!props.asyncOptions) return;
    const { groups: asyncGroups, options: asyncOptions } =
      await props.asyncOptions();
    if (asyncGroups) setGroups(asyncGroups);
    if (asyncOptions) setOptions(asyncOptions);
  };

  const onChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    _onChange?.(e);
    const { name: targetName, value: targetValue } = e.target;

    const meta = hasOptionMeta(targetValue, options);
    if (form && meta) {
      setRelatedFieldsFromMeta(formValues, meta, form, targetName);
    }

    if (hooks?.afterOnChange) {
      const selectedOption = findSelectedOption(targetValue, {
        groups,
        options,
      });
      if (selectedOption) {
        hooks.afterOnChange({ name: targetName, value: selectedOption });
      }
    }
  };

  const handleClear = () => {
    form.setValue(name, '', { shouldDirty: true });
  };

  useEffect(() => {
    resolveAsyncOptions();
  }, [props.asyncOptions]);

  useEffect(() => {
    setGroups(initialGroups);
  }, [initialGroups]);

  useEffect(() => {
    setOptions(initialOptions);
  }, [initialOptions]);

  return (
    <InputGroup
      endElement={
        isClearable &&
        value && (
          <IconButton
            aria-label="Clear selection"
            borderRadius={0}
            onClick={handleClear}
            size={props.size || 'md'}
            variant="ghost"
          >
            <CgClose />
          </IconButton>
        )
      }
    >
      <NativeSelect.Root
        ref={_ref}
        size={props.size || 'md'}
        style={{ textIndent: 0 }}
        {...props}
      >
        <NativeSelect.Field
          defaultValue={defaultValue}
          id={id || name}
          name={name}
          onBlur={onBlur}
          onChange={onChange}
          onFocus={onFocus}
          // value={value ?? defaultValue}
        >
          {hidePlaceholder !== true && (
            <option value="">{placeholder || 'Choose...'}</option>
          )}

          {groups?.map(({ label, options: groupOptions }) => (
            <optgroup key={label} label={label}>
              {groupOptions?.map(
                ({ label: optionLabel, value: optionValue }) => (
                  <option key={optionValue.toString()} value={optionValue}>
                    {optionLabel}
                  </option>
                ),
              )}
            </optgroup>
          ))}

          {options?.map(({ label, value: optionValue }) => (
            <option key={optionValue.toString()} value={optionValue}>
              {label}
            </option>
          ))}
        </NativeSelect.Field>
        <NativeSelect.Indicator
          boxSize={iconSize}
          color={iconColor}
          right={isClearable && value && '2.5rem'}
        >
          <CgChevronDown />
        </NativeSelect.Indicator>
      </NativeSelect.Root>
    </InputGroup>
  );
};

Select.controlled = false;
Select.displayName = 'Select';
